/**
 * @description
 * Stripe product service for managing products, prices, and Stripe integration.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 */

import { BaseAPIClient } from "../core/base-client";
import type { StripeProduct, StripePrice } from "./types";

/**
 * Stripe product service
 */
export class StripeProductService extends BaseAPIClient {
  /**
   * Get products
   */
  async getProducts(): Promise<StripeProduct[]> {
    const data = await this.request<{ products: StripeProduct[] }>('/stripe/products');
    return data.products;
  }

  /**
   * Create product
   */
  async createProduct(data: {
    name: string;
    description?: string;
    serviceType: 'vibesdk' | 'anthropic' | 'vscode_lens' | 'other';
    price: {
      unitAmount: number;
      currency: string;
      recurring?: { interval: 'month' | 'year'; intervalCount: number };
    };
  }): Promise<any> {
    const response = await this.request<{ product: any }>('/stripe/products', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.product;
  }

  /**
   * Get all prices from Stripe
   */
  async getAllPrices(): Promise<StripePrice[]> {
    const response = await this.request<{ prices: StripePrice[] }>('/stripe/prices');
    return response.prices;
  }

  /**
   * Sync Stripe products
   */
  async syncStripeProducts(): Promise<{ success: boolean; productsSynced: number; error?: string }> {
    const response = await this.request<{ success: boolean; productsSynced: number; error?: string }>('/stripe/sync-products');
    return response;
  }

  /**
   * Test Stripe connection
   */
  async testStripeConnection(): Promise<{ success: boolean; error?: string }> {
    const response = await this.request<{ success: boolean; error?: string }>('/stripe/test-connection');
    return response;
  }
}

// Export singleton instance
export const stripeProductService = new StripeProductService();
